import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import type { RoleDefinition } from "@/lib/types";
import { Badge, Card, PageHeader, Skeleton } from "@/components/ui";

/**
 * The role catalogue, rendered from the server's own permission table.
 *
 * Nothing here is hard-coded in the frontend. A role added on the API appears
 * on this page without a release, and what it shows is what the API actually
 * enforces rather than a second copy that drifts.
 */
export function PlatformRoles() {
  const { data, isLoading } = useQuery({
    queryKey: ["platform-roles"],
    queryFn: () => api.get<{ data: RoleDefinition[] }>("/v1/admin/roles"),
  });

  const staff = (data?.data ?? []).filter((role) => role.is_staff);
  const customer = (data?.data ?? []).filter((role) => !role.is_staff);

  if (isLoading) {
    return (
      <Card>
        <Skeleton className="h-64 w-full" />
      </Card>
    );
  }

  return (
    <>
      <PageHeader
        title="Roles"
        subtitle="What each role can do. This is read from the API's own permission table, not a copy."
      />

      <Section
        title="Customer roles"
        body="Assignable by an account owner or admin from their own team page."
        roles={customer}
      />
      <Section
        title="WeShip staff"
        body="Granted by a platform admin only. No self-service path can produce one of these."
        roles={staff}
      />
    </>
  );
}

function Section({ title, body, roles }: { title: string; body: string; roles: RoleDefinition[] }) {
  return (
    <section className="mb-8">
      <h2 className="text-lg font-bold tracking-tight">{title}</h2>
      <p className="mb-4 mt-1 text-sm text-ink-500">{body}</p>
      <div className="grid gap-4 lg:grid-cols-2">
        {roles.map((role) => (
          <Card key={role.role}>
            <div className="flex flex-wrap items-center gap-2">
              <h3 className="font-semibold">{role.label}</h3>
              <code className="rounded bg-ink-100 px-1.5 py-0.5 font-mono text-xs text-ink-600">
                {role.role}
              </code>
              {role.is_staff && <Badge tone="attention">staff</Badge>}
              {!role.assignable && !role.is_staff && <Badge tone="neutral">not assignable</Badge>}
            </div>
            <ul className="mt-3 flex flex-wrap gap-1.5">
              {role.permissions.length === 0 && (
                <li className="text-sm text-ink-500">No permissions.</li>
              )}
              {role.permissions.map((permission) => (
                <li
                  key={permission}
                  className="rounded-lg bg-ink-100 px-2 py-1 font-mono text-xs text-ink-600"
                >
                  {permission}
                </li>
              ))}
            </ul>
          </Card>
        ))}
      </div>
    </section>
  );
}
